import React, { useEffect, useRef, useState } from 'react';
import { GeneratedImage } from '../hooks/useImageGeneration';
import './CanvasDisplay.css';

interface CanvasDisplayProps {
  images: GeneratedImage[];
  onDeleteImage: (imageId: string) => void;
  imageLimit?: number;
  onClearAll?: () => void;
}

interface ImageObject {
  id: string;
  img: HTMLImageElement;
  x: number;
  y: number;
  width: number;
  height: number;
  velocityX: number;
  velocityY: number;
  rotation: number;
  rotationSpeed: number;
  scale: number;
  opacity: number;
  prompt: string;
}

const CanvasDisplay: React.FC<CanvasDisplayProps> = ({ images, onDeleteImage, imageLimit = 500, onClearAll }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageObjectsRef = useRef<ImageObject[]>([]);
  const [imageObjects, setImageObjects] = useState<ImageObject[]>([]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showCopyConfirmation, setShowCopyConfirmation] = useState(false);
  const animationFrameRef = useRef<number>(0);
  const isDraggingRef = useRef<boolean>(false);
  const draggedImageRef = useRef<ImageObject | null>(null);
  const mousePositionRef = useRef({ x: 0, y: 0 });
  const lastTapRef = useRef<number>(0);
  const touchStartPosRef = useRef({ x: 0, y: 0 });
  const potentialDragImageRef = useRef<ImageObject | null>(null);

  // Seeded random function for consistent positions
  const seededRandom = (seed: string) => {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = ((hash << 5) - hash) + seed.charCodeAt(i);
      hash = hash & hash;
    }
    const x = Math.sin(hash++) * 10000;
    return x - Math.floor(x);
  };

  // Get next position in a flowing grid layout
  const getNextPosition = (
    width: number,
    height: number,
    existingObjects: ImageObject[]
  ): { x: number; y: number } => {
    // Use image ID hash as seed for consistent but varied positioning
    const imageCount = existingObjects.length;

    // Adjust layout for mobile
    const isMobile = window.innerWidth <= 768;
    const padding = isMobile ? 20 : 80;

    // Create a more spread out grid layout
    // Desktop: wider spacing, Mobile: tighter spacing
    const cols = isMobile ? Math.ceil(Math.sqrt(imageCount + 1) * 1.5) : Math.ceil(Math.sqrt(imageCount + 1) * 2);
    const gridWidth = window.innerWidth - (padding * 2);
    const gridHeight = window.innerHeight - (padding * 2);

    const col = imageCount % cols;
    const row = Math.floor(imageCount / cols);

    const cellWidth = gridWidth / cols;
    const cellHeight = gridHeight / Math.ceil((imageCount + 1) / cols);

    // Add more randomness within the cell for desktop
    const randomFactor = isMobile ? 0.3 : 0.5;
    const randomX = (Math.random() - 0.5) * cellWidth * randomFactor;
    const randomY = (Math.random() - 0.5) * cellHeight * randomFactor;

    const x = padding + col * cellWidth + (cellWidth - width) / 2 + randomX;
    const y = padding + row * cellHeight + (cellHeight - height) / 2 + randomY;

    // Keep within bounds
    return {
      x: Math.max(padding, Math.min(x, window.innerWidth - width - padding)),
      y: Math.max(padding, Math.min(y, window.innerHeight - height - padding))
    };
  };

  // Load images and create image objects
  useEffect(() => {
    const loadImages = async () => {
      const loadedImages: ImageObject[] = [];

      for (const image of images) {
        const img = new Image();
        img.crossOrigin = 'anonymous';

        await new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve; // Continue even if image fails
          img.src = image.url;
        });

        // Check if image already exists
        const existingImage = imageObjectsRef.current.find(obj => obj.id === image.id);
        if (!existingImage && img.complete && img.naturalWidth > 0) {
          // Use seeded random for consistent properties
          const seed3 = seededRandom(image.id + 'scale');
          const seed4 = seededRandom(image.id + 'opacity');

          // Calculate aspect ratio to maintain proportions
          const aspectRatio = img.naturalWidth / img.naturalHeight;
          // Use smaller images on mobile
          const isMobile = window.innerWidth <= 768;
          const baseHeight = isMobile ? 250 : 400;
          const width = baseHeight * aspectRatio;
          const height = baseHeight;

          // Get next position in grid layout
          const allExisting = [...imageObjectsRef.current, ...loadedImages];
          const position = getNextPosition(width, height, allExisting);

          loadedImages.push({
            id: image.id,
            img,
            x: position.x,
            y: position.y,
            width,
            height,
            velocityX: 0,
            velocityY: 0,
            rotation: 0,
            rotationSpeed: 0,
            scale: 0.8 + seed3 * 0.4,
            opacity: 0.9 + seed4 * 0.1,
            prompt: image.prompt
          });
        }
      }

      if (loadedImages.length > 0) {
        imageObjectsRef.current = [...imageObjectsRef.current, ...loadedImages];
        setImageObjects(imageObjectsRef.current);
      }
    };

    loadImages();
  }, [images]);

  // Animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const animate = () => {
      // Clear canvas with white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Update and draw each image
      imageObjectsRef.current.forEach(obj => {
        // Update position if being dragged
        if (draggedImageRef.current?.id === obj.id) {
          obj.x = mousePositionRef.current.x - obj.width / 2;
          obj.y = mousePositionRef.current.y - obj.height / 2;
        }

        // Draw image with white background removal
        ctx.save();
        ctx.globalAlpha = obj.opacity;
        ctx.translate(obj.x + obj.width / 2, obj.y + obj.height / 2);
        ctx.scale(obj.scale, obj.scale);

        try {
          // Create a temporary canvas to process the image
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = obj.img.width;
          tempCanvas.height = obj.img.height;
          const tempCtx = tempCanvas.getContext('2d');

          if (tempCtx) {
            // Draw the image to temp canvas
            tempCtx.drawImage(obj.img, 0, 0);

            // Get image data
            const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
            const data = imageData.data;

            // Remove white/near-white pixels
            for (let i = 0; i < data.length; i += 4) {
              const r = data[i];
              const g = data[i + 1];
              const b = data[i + 2];

              // If pixel is white or near-white (threshold of 240), make it transparent
              if (r > 240 && g > 240 && b > 240) {
                data[i + 3] = 0; // Set alpha to 0 (transparent)
              }
            }

            // Put the processed image data back
            tempCtx.putImageData(imageData, 0, 0);

            // Draw the processed image to main canvas
            ctx.drawImage(
              tempCanvas,
              -obj.width / 2,
              -obj.height / 2,
              obj.width,
              obj.height
            );
          }
        } catch (error) {
          console.error('Error drawing image:', error);
          // Fallback to drawing without processing
          try {
            ctx.drawImage(
              obj.img,
              -obj.width / 2,
              -obj.height / 2,
              obj.width,
              obj.height
            );
          } catch (fallbackError) {
            console.error('Fallback drawing also failed:', fallbackError);
          }
        }

        // Draw border if selected
        if (selectedImage === obj.id) {
          ctx.strokeStyle = '#667eea';
          ctx.lineWidth = 3;
          ctx.strokeRect(-obj.width / 2, -obj.height / 2, obj.width, obj.height);
        }

        ctx.restore();
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [selectedImage]);

  // Handle canvas resize
  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Add touch event listeners with { passive: false } to allow preventDefault
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleTouchStartNative = (e: TouchEvent) => {
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0];
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;

      touchStartPosRef.current = { x, y };
      mousePositionRef.current = { x, y };

      const now = Date.now();
      const timeSinceLastTap = now - lastTapRef.current;

      console.log('Touch start:', { timeSinceLastTap, lastTap: lastTapRef.current, now });

      for (let i = imageObjectsRef.current.length - 1; i >= 0; i--) {
        const obj = imageObjectsRef.current[i];
        if (
          x >= obj.x &&
          x <= obj.x + obj.width &&
          y >= obj.y &&
          y <= obj.y + obj.height
        ) {
          console.log('Tapped on image:', obj.id, 'timeSinceLastTap:', timeSinceLastTap);

          if (timeSinceLastTap < 500 && timeSinceLastTap > 0) {
            e.preventDefault();
            console.log('✓ DOUBLE TAP DETECTED! Copying to clipboard');

            if (navigator.vibrate) {
              navigator.vibrate(50);
            }

            copyToClipboard(obj);
            lastTapRef.current = 0;
            potentialDragImageRef.current = null;
            return;
          }

          console.log('First tap recorded');
          lastTapRef.current = now;
          potentialDragImageRef.current = obj;
          setSelectedImage(obj.id);
          break;
        }
      }
    };

    const handleTouchMoveNative = (e: TouchEvent) => {
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0];
      const newX = touch.clientX - rect.left;
      const newY = touch.clientY - rect.top;

      mousePositionRef.current = { x: newX, y: newY };

      const moveDistance = Math.sqrt(
        Math.pow(newX - touchStartPosRef.current.x, 2) +
        Math.pow(newY - touchStartPosRef.current.y, 2)
      );

      if (moveDistance > 10 && potentialDragImageRef.current && !isDraggingRef.current) {
        isDraggingRef.current = true;
        draggedImageRef.current = potentialDragImageRef.current;
      }
    };

    const handleTouchEndNative = () => {
      isDraggingRef.current = false;
      draggedImageRef.current = null;
      potentialDragImageRef.current = null;
    };

    canvas.addEventListener('touchstart', handleTouchStartNative, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMoveNative, { passive: false });
    canvas.addEventListener('touchend', handleTouchEndNative);

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStartNative);
      canvas.removeEventListener('touchmove', handleTouchMoveNative);
      canvas.removeEventListener('touchend', handleTouchEndNative);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mouse event handlers
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Check if clicking on an image
    for (let i = imageObjectsRef.current.length - 1; i >= 0; i--) {
      const obj = imageObjectsRef.current[i];
      if (
        x >= obj.x &&
        x <= obj.x + obj.width &&
        y >= obj.y &&
        y <= obj.y + obj.height
      ) {
        isDraggingRef.current = true;
        draggedImageRef.current = obj;
        setSelectedImage(obj.id);
        break;
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    mousePositionRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
    draggedImageRef.current = null;
  };

  const handleDoubleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Check if double-clicking on an image
    for (let i = imageObjectsRef.current.length - 1; i >= 0; i--) {
      const obj = imageObjectsRef.current[i];
      if (
        x >= obj.x &&
        x <= obj.x + obj.width &&
        y >= obj.y &&
        y <= obj.y + obj.height
      ) {
        // Double click/tap = copy to clipboard
        e.preventDefault();
        copyToClipboard(obj);
        break;
      }
    }
  };



  const copyToClipboard = async (obj: ImageObject) => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = obj.img.width;
      canvas.height = obj.img.height;
      const ctx = canvas.getContext('2d');

      if (ctx) {
        // Draw the image
        ctx.drawImage(obj.img, 0, 0);

        // Get image data and remove white background
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Remove white/near-white pixels (make transparent)
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];

          // If pixel is white or near-white (threshold of 240), make it transparent
          if (r > 240 && g > 240 && b > 240) {
            data[i + 3] = 0; // Set alpha to 0 (transparent)
          }
        }

        // Put the processed image data back
        ctx.putImageData(imageData, 0, 0);

        const blob = await new Promise<Blob | null>((resolve) => {
          canvas.toBlob((blob) => resolve(blob), 'image/png');
        });

        if (blob) {
          // Try native share API first (best for mobile - allows save to Photos)
          if (navigator.share && navigator.canShare) {
            try {
              const file = new File([blob], `sticker-${obj.prompt.replace(/\s+/g, '-')}.png`, { type: 'image/png' });

              if (navigator.canShare({ files: [file] })) {
                await navigator.share({
                  files: [file]
                });

                // Show confirmation message
                setShowCopyConfirmation(true);
                setTimeout(() => setShowCopyConfirmation(false), 2000);
                return;
              }
            } catch (shareError: any) {
              // User cancelled share or share not available
              if (shareError.name !== 'AbortError') {
                console.log('Share failed, trying clipboard');
              } else {
                // User cancelled - don't show error
                return;
              }
            }
          }

          // Try clipboard second (works on desktop and some browsers)
          try {
            await navigator.clipboard.write([
              new ClipboardItem({ 'image/png': blob })
            ]);

            // Show confirmation message
            setShowCopyConfirmation(true);
            setTimeout(() => setShowCopyConfirmation(false), 2000);
          } catch (clipboardError) {
            // Final fallback: auto-download
            console.log('Clipboard not supported, downloading instead');
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `sticker-${obj.prompt.replace(/\s+/g, '-')}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            // Show download confirmation
            setShowCopyConfirmation(true);
            setTimeout(() => setShowCopyConfirmation(false), 2000);
          }
        }
      }
    } catch (error) {
      console.error('Failed to copy:', error);
      alert('Failed to save sticker. Please try again.');
    }
  };

  const imagesRemaining = imageLimit - images.length;

  return (
    <>
      {onClearAll && (
        <button className="clear-all-button" onClick={onClearAll}>
          Clear All & Start Fresh
        </button>
      )}
      {showCopyConfirmation && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(0, 0, 0, 0.85)',
          color: 'white',
          padding: '20px 40px',
          borderRadius: '15px',
          fontSize: '1.2rem',
          fontWeight: '600',
          zIndex: 1000,
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
          animation: 'fadeIn 0.2s ease-in-out',
        }}>
          ✓ Sticker saved!
        </div>
      )}
      <canvas
        ref={canvasRef}
        className="image-canvas"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onDoubleClick={handleDoubleClick}
      />
    </>
  );
};

export default CanvasDisplay;